/**
 * «Клипы» (спека §9.2 «Видео» п.2): таблица кадры × модели. В строке кадра — его СТАРТОВЫЙ ФРЕЙМ
 * (картинка кадра, с которой модель начнёт движение, №331) и последний клип модели: постер
 * (клик → плеер в `Dialog`), статус (очередь / отправлен / готово / ошибка с текстом), цена (факт или
 * ≈ оценка), длительность файла; «отменить» — только в очереди (отправленный уже оплачен), «повторить»
 * — у упавшего или отменённого (ПЛАТНО, ключ на клип до ответа — `useRetryClip`).
 *
 * Данные — опрос `GET /api/projects/{id}/video` (`useProjectVideo`): раз в 5 с, пока клипы в работе.
 * mp4 никогда не идёт в `<img>`: миниатюра — постер (кадр 0 с), сам клип — только `<video>`.
 */

import clsx from "clsx";
import { Play, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Btn, Empty, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { Dialog } from "@/components/trends/Dialog";
import { mediaUrl } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import {
  CLIP_STATUS_LABEL,
  seconds,
  useCancelClip,
  useRetryClip,
  useVideoOptions,
  type TimelineClip,
  type TimelineShot,
} from "@/lib/video";
import { clipModels } from "@/lib/videoRun";

import { CLIP_PILL, useModelLabels } from "./clips";

export function ClipsTable({ pid, timeline, pending }: { pid: number; timeline: TimelineShot[]; pending: number }) {
  const options = useVideoOptions();
  const label = useModelLabels();
  const cancel = useCancelClip(pid);
  const retry = useRetryClip(pid);
  const [open, setOpen] = useState<{ clip: TimelineClip; shot: TimelineShot } | null>(null);
  const models = clipModels(timeline, (options.data?.models ?? []).map((m) => m.code));

  return (
    <Panel title="Клипы" aside={pending > 0 ? `в очереди и в работе: ${pending} · обновляется само` : undefined} annot="клипы ролика">
      {models.length === 0 ? (
        <div className="grid gap-2">
          <Empty>Клипов нет — запустите по моделям.</Empty>
          {/* Стартовые фреймы показываем и до первого клипа (ревью 16.10): именно в этот момент
              сотрудник решает, что оживлять, а таблица с картинками появляется только после запуска. */}
          {timeline.some((s) => s.still) ? (
            <div className="grid gap-1.5 px-3 pb-3">
              <Hint>стартовые фреймы — с них модель начнёт движение:</Hint>
              <div className="flex flex-wrap gap-2">
                {timeline.map((shot) =>
                  shot.still ? (
                    <Link
                      key={shot.shot_id}
                      to={`/project/${pid}/shot/${shot.shot_id}`}
                      title={`${shot.order}. ${shot.title}`}
                      className="grid justify-items-center gap-1"
                    >
                      <img
                        src={mediaUrl(shot.still.asset_path)}
                        alt=""
                        loading="lazy"
                        className="h-[85px] w-[48px] rounded-[3px] border border-border bg-bg-subtle object-contain"
                      />
                      <span className="text-[11px] text-fg-faint">{shot.order}</span>
                    </Link>
                  ) : null,
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-left text-[11.5px] text-fg-faint">
                <th className="py-1.5 pr-3 font-normal">кадр</th>
                {models.map((code) => (
                  <th key={code} className="py-1.5 pr-3 font-normal">
                    {label(code)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeline.map((shot) => (
                <tr key={shot.shot_id} className="border-b border-border align-top">
                  <td className="py-2 pr-3">
                    {/* Стартовый фрейм клипа — картинка кадра (задача Оли №331: «создание видео
                        фото ракурса = стартовый фрейм»): раньше в строке стояло только название. */}
                    <div className="flex items-start gap-2">
                      {shot.still ? (
                        <Link to={`/project/${pid}/shot/${shot.shot_id}`} title="стартовый фрейм клипа">
                          <img
                            src={mediaUrl(shot.still.asset_path)}
                            alt=""
                            loading="lazy"
                            className="h-[85px] w-[48px] flex-none rounded-[3px] border border-border bg-bg-subtle object-contain"
                          />
                        </Link>
                      ) : (
                        <span className="grid h-[85px] w-[48px] flex-none place-items-center rounded-[3px] border border-dashed border-border-strong text-[10px] text-fg-faint">
                          нет
                        </span>
                      )}
                      <div className="min-w-0">
                        <Link to={`/project/${pid}/shot/${shot.shot_id}`} className="hover:text-accent-text">
                          {shot.order}. {shot.title}
                        </Link>
                        <Hint>{seconds(shot.duration_ms)}</Hint>
                      </div>
                    </div>
                  </td>
                  {models.map((code) => {
                    const clip = shot.clips.find((c) => c.model === code);
                    if (!clip)
                      return (
                        <td key={code} className="py-2 pr-3 text-fg-faint">
                          —
                        </td>
                      );
                    const cancelling = cancel.isPending && cancel.variables === clip.generation_id;
                    const retrying = retry.pending === clip.generation_id;
                    // Перегенерация поверх готового: сам клип показываем прежний (он оплачен), а её
                    // статус — отдельной строкой; второй раз запускать, пока идёт, не даём.
                    const again = clip.pending;
                    return (
                      <td key={code} className="py-2 pr-3" data-annot={`клип ${clip.generation_id}`}>
                        <div className="flex gap-2">
                          <Poster clip={clip} onOpen={() => setOpen({ clip, shot })} />
                          <div className="grid content-start gap-1">
                            <span
                              className={clsx(
                                "w-fit rounded-full px-2 py-0.5 text-[11.5px]",
                                CLIP_PILL[clip.status],
                              )}
                            >
                              {CLIP_STATUS_LABEL[clip.status]}
                            </span>
                            <span className="tnum text-[12px]">
                              {clip.cost_actual !== null ? (
                                usd(clip.cost_actual)
                              ) : clip.cost_estimate !== null ? (
                                <i className="text-fg-muted">≈ {usd(clip.cost_estimate)}</i>
                              ) : (
                                <span className="text-fg-faint">цены нет</span>
                              )}
                            </span>
                            {clip.duration_ms !== null ? <Hint>файл {seconds(clip.duration_ms)}</Hint> : null}
                            {again ? (
                              <span
                                className={clsx(
                                  "w-fit rounded-full px-2 py-0.5 text-[11.5px]",
                                  CLIP_PILL[again.status],
                                )}
                                title={again.error ?? undefined}
                              >
                                перегенерация: {CLIP_STATUS_LABEL[again.status]}
                              </span>
                            ) : null}
                            {/* Промпт ЗАПУСКА (задача Оли №337: «должно быть видно промт по
                                которому генерировалось видео»): поля кадра с тех пор могли
                                поменяться, а здесь факт — что ушло в модель. */}
                            {clip.prompt ? (
                              <details className="max-w-[260px] text-[11px] text-fg-faint">
                                <summary className="cursor-pointer select-none hover:text-fg">промпт</summary>
                                <p className="mt-1 whitespace-pre-wrap leading-snug text-fg-muted">{clip.prompt}</p>
                              </details>
                            ) : null}
                            {clip.error ? (
                              <div className="max-w-[220px] text-[11.5px] leading-snug text-err">{clip.error}</div>
                            ) : null}
                            <div className="flex flex-wrap gap-1">
                              {clip.status === "queued" ? (
                                <Btn
                                  tone="ghost"
                                  className="h-6 px-1.5"
                                  disabled={cancelling}
                                  onClick={() => cancel.mutate(clip.generation_id)}
                                >
                                  <X size={12} />
                                  {cancelling ? "отменяю…" : "отменить"}
                                </Btn>
                              ) : null}
                              {/* Перегенерация и у ГОТОВОГО клипа (задача Оли №337: «возможность
                                  перегенерировать в случае неудачи» — неудачным бывает и удавшийся
                                  файл). Клип в очереди или в работе не трогаем: он уже оплачен. */}
                              {(clip.status === "failed" || clip.status === "cancelled" || clip.status === "done") &&
                              (again === null || again.status === "failed" || again.status === "cancelled") ? (
                                <Btn
                                  className="h-6 px-1.5"
                                  disabled={retry.busy}
                                  onClick={() => retry.run(clip.generation_id)}
                                  title="ПЛАТНО: новый клип с тем же снимком и промптом"
                                >
                                  <RotateCcw size={12} />
                                  {retrying
                                    ? "ставлю…"
                                    : `${clip.status === "done" ? "перегенерировать" : "повторить"}${clip.cost_estimate !== null ? ` ≈ ${usd(clip.cost_estimate, 2)}` : ""}`}
                                </Btn>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ErrorLine text={cancel.isError ? errorText(cancel.error) : ""} className="mt-2" />
      <ErrorLine text={retry.error} className="mt-2" />

      <Dialog
        open={open !== null}
        title={open ? `Кадр ${open.shot.order} · ${label(open.clip.model)}` : ""}
        onClose={() => setOpen(null)}
      >
        {open?.clip.asset_path ? (
          <div className="grid gap-2 p-4">
            <video
              controls
              muted
              autoPlay
              playsInline
              preload="metadata"
              poster={open.clip.poster_path ? mediaUrl(open.clip.poster_path) : undefined}
              src={mediaUrl(open.clip.asset_path)}
              className="mx-auto max-h-[70vh] w-auto rounded-[var(--radius-ui)] bg-black"
            />
            {open.clip.variant_id !== null ? (
              <Link to={`/results/${open.clip.variant_id}`} className="text-[12px] text-accent-text hover:underline">
                карточка клипа →
              </Link>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </Panel>
  );
}

/** Постер готового клипа с ▶ (кнопка → плеер); иначе — пустая рамка по статусу. */
function Poster({ clip, onOpen }: { clip: TimelineClip; onOpen: () => void }) {
  const box = "relative grid h-[85px] w-12 flex-none place-items-center overflow-hidden rounded-[4px] border";
  if (clip.status === "done" && clip.asset_path)
    return (
      <button type="button" className={clsx(box, "border-border bg-black")} onClick={onOpen} title="смотреть клип">
        {clip.poster_path ? (
          <img src={mediaUrl(clip.poster_path)} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
        ) : null}
        <span className="relative grid size-5 place-items-center rounded-full bg-bg/80 text-fg">
          <Play size={11} fill="currentColor" />
        </span>
      </button>
    );
  return (
    <div className={clsx(box, "border-dashed border-border-strong text-[11px] text-fg-faint")}>
      {clip.status === "queued" || clip.status === "running" ? "…" : "—"}
    </div>
  );
}
